/**
 * Language Manager Module
 * Manages multiple language support, detection, and configuration
 *
 * Performance: franc is lazy-loaded only when detectLanguage() is called
 */

import type {
  LanguageCode,
  LanguageConfig,
  SupportedLanguages,
} from './language-constants';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './language-constants';
import { LANGUAGE_DETECTION } from './app-constants';
import { logger } from './logger';

// Lazy-loaded franc module
let francModule: typeof import('franc-min') | null = null;

// Type definitions
export interface LanguageOption {
  code: LanguageCode;
  label: string;
  name: string;
  nativeName: string;
}

export interface LanguageStats {
  totalSupported: number;
  supportedCodes: string[];
  defaultLanguage: string;
  francMapping: Record<string, string>;
}

export interface DetectionOptions {
  minLength?: number;
}

class LanguageManager {
  private supportedLanguages: SupportedLanguages;
  private francToLanguageMap: Record<string, string>;
  private defaultLanguage: string;

  constructor() {
    this.supportedLanguages = SUPPORTED_LANGUAGES;
    this.francToLanguageMap = {};
    this.initializeFrancMapping();
    this.defaultLanguage = DEFAULT_LANGUAGE;
  }

  /**
   * Initialize mapping from franc language codes to our language codes
   */
  private initializeFrancMapping(): void {
    Object.keys(this.supportedLanguages).forEach((langCode) => {
      const francCode =
        this.supportedLanguages[langCode as LanguageCode].francCode;
      this.francToLanguageMap[francCode] = langCode;
    });
  }

  /**
   * Get all supported languages
   * @returns Object with language configurations
   */
  getSupportedLanguages(): SupportedLanguages {
    return this.supportedLanguages;
  }

  /**
   * Get language codes
   * @returns Array of language codes
   */
  getLanguageCodes(): string[] {
    return Object.keys(this.supportedLanguages);
  }

  /**
   * Get language configuration by code
   * @param langCode - Language code
   * @returns Language configuration or null if not supported
   */
  getLanguageConfig(langCode: string): LanguageConfig | null {
    return this.supportedLanguages[langCode as LanguageCode] || null;
  }

  /**
   * Check if a language is supported
   * @param langCode - Language code
   * @returns True if supported
   */
  isLanguageSupported(langCode: string): boolean {
    return langCode in this.supportedLanguages;
  }

  /**
   * Detect language of given text using franc library
   * Franc is lazy-loaded on first call for better initial load performance
   * @param text - Text to analyze
   * @param options - Detection options
   * @returns Detected language code or default language
   */
  async detectLanguage(
    text: string,
    options: DetectionOptions = {}
  ): Promise<string> {
    const minLength = options.minLength || LANGUAGE_DETECTION.MIN_TEXT_LENGTH;

    try {
      if (!text || typeof text !== 'string' || text.length < minLength) {
        return this.defaultLanguage;
      }

      // Lazy load franc on first use. franc-min (#133): the trigrams of the
      // ~80 most spoken languages instead of ~190; all five of ours are in it,
      // with the same results on our samples, for about half the download.
      if (!francModule) {
        francModule = await import('franc-min');
      }

      // Use franc to detect language
      const francResult = francModule.franc(text, {
        only: Object.values(this.supportedLanguages).map(
          (lang) => lang.francCode
        ),
      });

      // Check if we got a valid result
      if (
        francResult &&
        francResult !== 'und' &&
        this.francToLanguageMap[francResult]
      ) {
        return this.francToLanguageMap[francResult];
      }

      // Fallback to default language
      return this.defaultLanguage;
    } catch (error) {
      logger.warn('Language detection failed:', error);
      return this.defaultLanguage;
    }
  }

  /**
   * Get display name for language
   * @param langCode - Language code
   * @param native - Whether to return native name
   * @returns Language display name
   */
  getLanguageDisplayName(langCode: string, native: boolean = false): string {
    const config = this.getLanguageConfig(langCode);
    if (!config) return 'Unknown';
    return native ? config.nativeName : config.name;
  }

  /**
   * Get default language
   * @returns Default language code
   */
  getDefaultLanguage(): string {
    return this.defaultLanguage;
  }

  /**
   * Get language options for dropdown components
   * @param includeNativeNames - Include native names in labels
   * @returns Array of language options
   */
  getLanguageOptions(includeNativeNames: boolean = true): LanguageOption[] {
    return (Object.keys(this.supportedLanguages) as LanguageCode[]).map(
      (langCode) => {
        const config = this.supportedLanguages[langCode];
        let label = config.name;
        if (includeNativeNames && config.nativeName !== config.name) {
          label = `${config.name} (${config.nativeName})`;
        }
        return {
          code: langCode,
          label: label,
          name: config.name,
          nativeName: config.nativeName,
        };
      }
    );
  }

  /**
   * Validate language code
   * @param langCode - Language code to validate
   * @returns Valid language code or default language
   */
  validateLanguageCode(langCode: string): string {
    return this.isLanguageSupported(langCode) ? langCode : this.defaultLanguage;
  }
}

// Export to window for debugging (dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.LanguageManager = LanguageManager;
}

export default LanguageManager;
